import { useMemo, useState } from 'react'
import { ExternalLink, Inbox, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ActivityCode, CalendarView, PendingTask } from '@/api/resourceCalendarApi'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useTaskQueue } from '@/hooks/useResourceCalendar'
import { ACTIVITY_CODES, dayHeader, filterQueue, formatHours, queueCounts, type QueueView } from '@/lib/myTasks'
import { cn } from '@/lib/utils'
import { TypeBadge } from './ActivityCard'

const VIEWS: { id: QueueView; label: string }[] = [
  { id: 'unassigned', label: 'Not assigned' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'all', label: 'All' },
]

/**
 * Every pending task, whatever its type, waiting to be put on someone's day.
 * A supervisor sees everyone's and hands it out: equipment and cleaning are
 * assigned on the task itself, the rest are planned onto the calendar. Anyone
 * else sees only what is assigned to them — the server gives nothing more.
 */
export function TaskQueue({
  scope,
  canManage,
  legend,
  onAssign,
  onPlan,
}: {
  scope: 'own' | 'all'
  canManage: boolean
  legend: CalendarView['legend']
  /** Equipment / cleaning: change the owner on the task itself. */
  onAssign: (task: PendingTask) => void
  /** Everything else: plan it onto someone's day. */
  onPlan: (task: PendingTask) => void
}) {
  const queue = useTaskQueue()
  const [view, setView] = useState<QueueView>(scope === 'all' ? 'unassigned' : 'all')
  const [type, setType] = useState<ActivityCode | undefined>(undefined)
  const [search, setSearch] = useState('')

  const tasks = useMemo(() => queue.data ?? [], [queue.data])
  const counts = useMemo(() => queueCounts(tasks), [tasks])
  const rows = useMemo(() => filterQueue(tasks, { status: view, type, search }), [tasks, view, type, search])
  const nameOf = (code: string) => legend.find((l) => l.code === code)?.name ?? code
  const own = scope === 'own'

  return (
    <section
      aria-label={own ? 'My pending tasks' : 'Task queue'}
      className='flex min-h-0 flex-1 flex-col rounded-2xl border bg-card shadow-sm'
    >
      <div className='flex flex-wrap items-center gap-3 border-b px-4 py-3'>
        <h2 className='text-sm font-semibold'>{own ? 'My pending tasks' : 'Task queue'}</h2>
        {!own && (
          <div role='group' aria-label='Owner' className='flex rounded-lg bg-muted/60 p-0.5'>
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type='button'
                aria-pressed={view === v.id}
                onClick={() => setView(v.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground',
                  view === v.id && 'bg-card font-medium text-primary shadow-sm',
                )}
              >
                {v.label}
                <span className='tabular-nums opacity-70'>{counts[v.id]}</span>
              </button>
            ))}
          </div>
        )}
        <label className='ml-auto flex h-8 items-center gap-2 rounded-lg px-2.5 ring-1 ring-border focus-within:ring-2 focus-within:ring-primary/40'>
          <Search className='h-3.5 w-3.5 text-muted-foreground' />
          <input
            aria-label='Search the queue'
            placeholder='Search task, reference or owner'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className='w-56 bg-transparent text-sm outline-none placeholder:text-muted-foreground'
          />
        </label>
      </div>

      {/* Type chips, with how many of each are waiting */}
      <div role='group' aria-label='Type' className='flex flex-wrap gap-1.5 border-b px-4 py-2'>
        <button
          type='button'
          aria-pressed={!type}
          onClick={() => setType(undefined)}
          className={cn(
            'rounded-full border px-2.5 py-0.5 text-xs transition-colors hover:bg-muted',
            !type && 'border-primary/40 bg-primary/10 font-medium text-primary',
          )}
        >
          All types <span className='tabular-nums opacity-70'>{counts.all}</span>
        </button>
        {ACTIVITY_CODES.filter((c) => counts.byType[c]).map((c) => (
          <button
            key={c}
            type='button'
            aria-pressed={type === c}
            onClick={() => setType(type === c ? undefined : c)}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors hover:bg-muted',
              type === c && 'border-primary/40 bg-primary/10 font-medium text-primary',
            )}
          >
            <TypeBadge code={c} className='h-4 min-w-[1.4rem] text-[9px]' />
            {nameOf(c)}
            <span className='tabular-nums opacity-70'>{counts.byType[c]}</span>
          </button>
        ))}
      </div>

      <div className='overflow-x-persistent min-h-0 flex-1 overflow-y-auto'>
        {queue.isLoading ? (
          <p className='p-6 text-sm text-muted-foreground'>Loading pending work…</p>
        ) : queue.isError ? (
          <p className='p-6 text-sm text-destructive'>{queue.error.message}</p>
        ) : rows.length === 0 ? (
          <div className='flex flex-col items-center gap-1 p-10 text-center text-sm text-muted-foreground'>
            <Inbox className='h-6 w-6' />
            {own ? 'Nothing pending is assigned to you.' : 'Nothing waiting here.'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className='text-right'>Hours</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Planned</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={`${t.kind}:${t.id}`}>
                  <TableCell>
                    <span className='flex items-center gap-2'>
                      <TypeBadge code={t.type} />
                      <span className='font-medium'>{t.description}</span>
                    </span>
                  </TableCell>
                  <TableCell>
                    {t.href && t.sourceRef ? (
                      <Link to={t.href} className='text-primary underline-offset-2 hover:underline'>
                        {t.sourceRef}
                      </Link>
                    ) : (
                      (t.sourceRef ?? '—')
                    )}
                  </TableCell>
                  <TableCell className='tabular-nums'>{t.dueDate ? dayHeader(t.dueDate) : '—'}</TableCell>
                  <TableCell className='text-right tabular-nums'>{formatHours(t.hours)}</TableCell>
                  <TableCell>{t.ownerName ?? <span className='text-muted-foreground'>Not assigned</span>}</TableCell>
                  <TableCell className='tabular-nums text-muted-foreground'>
                    {t.plannedDate ? dayHeader(t.plannedDate) : 'Not planned'}
                  </TableCell>
                  <TableCell className='text-right'>
                    <span className='inline-flex items-center gap-1.5'>
                      {t.href && (
                        <Button size='sm' variant='ghost' asChild>
                          <Link to={t.href} title='Open this task'>
                            Open <ExternalLink className='ml-1 h-3.5 w-3.5' />
                          </Link>
                        </Button>
                      )}
                      {canManage &&
                        (t.kind === 'maintenance' ? (
                          <Button size='sm' variant='outline' onClick={() => onAssign(t)}>
                            {t.ownerId ? 'Reassign' : 'Assign'}
                          </Button>
                        ) : (
                          <Button size='sm' variant='outline' onClick={() => onPlan(t)}>
                            Plan
                          </Button>
                        ))}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}
